import React, { lazy, Suspense, useEffect, useState } from 'react';
import { isTerminalShortcut, OPEN_TERMINAL_EVENT } from './terminal-events';

const loadTerminal = () => import('./terminal');
const Terminal = lazy(loadTerminal);

// Costs almost nothing until used: it only listens for the open request. The
// first time one arrives it loads the terminal's code, then hands over to it
// (the terminal opens itself and manages its own shortcut from then on).
function TerminalLauncher() {
    const [loaded, setLoaded] = useState(false);

    // Fetch the code quietly once the page is idle, so the first open is instant
    // and the terminal also works offline. It never competes with the first paint.
    useEffect(() => {
        if (typeof window.requestIdleCallback === 'function') {
            const id = window.requestIdleCallback(loadTerminal, { timeout: 6000 });
            return () => window.cancelIdleCallback(id);
        }
        const id = setTimeout(loadTerminal, 4000);
        return () => clearTimeout(id);
    }, []);

    useEffect(() => {
        if (loaded) return undefined;
        const request = () => setLoaded(true);
        const onKey = (event) => {
            if (isTerminalShortcut(event)) {
                event.preventDefault();
                request();
            }
        };
        document.addEventListener('keydown', onKey);
        window.addEventListener(OPEN_TERMINAL_EVENT, request);
        return () => {
            document.removeEventListener('keydown', onKey);
            window.removeEventListener(OPEN_TERMINAL_EVENT, request);
        };
    }, [loaded]);

    if (!loaded) return null;
    return (
        <Suspense fallback={null}>
            <Terminal startOpen />
        </Suspense>
    );
}

export default TerminalLauncher;
